import {currencyFormatter} from "../../utils/helpers";
import {Nullable, RatingStatisticsType} from "../../definitions/types";
import {RatingStatisticsModel} from "./ratingStatisticsModel";

/**
 * @class
 * @description Represents a cross sell model for a product
 */
export class CrossSellModel {
    private readonly _product: {
        collectionName?: string;
        customizeIt: boolean;
        designerId?: number;
        id: number;
        isDynamicDimension?: boolean;
        minQuantity: number;
        primaryId?: number;
        sku: string;
        title?: string;
        variantPath: string;
    };
    private readonly _image: {
        alt?: string;
        title?: string;
        url: string
    };
    private readonly _price: {
        onSale: boolean;
        price: number;
        saleAmount?: number;
        salePercentage?: number;
        unitPrice: number
    };
    private readonly _ratingStatistics: RatingStatisticsModel;

    /**
     * @constructor
     * @description Creates a new instance of the model
     * @param product
     * @param image
     * @param price
     * @param ratingStatistics
     */
    constructor(
        product: {
            collectionName?: string,
            customizeIt: boolean,
            designerId?: number,
            id: number,
            isDynamicDimension?: boolean,
            minQuantity: number,
            primaryId?: number,
            sku: string,
            title?: string,
            variantPath: string
        },
        image: {
            alt?: string,
            title?: string,
            url: string
        },
        price: {
            onSale: boolean,
            price: number,
            saleAmount?: number,
            salePercentage?: number,
            unitPrice: number
        },
        ratingStatistics: Nullable<RatingStatisticsType>
    ) {
        this._product = product
        this._image = image;
        this._price = price;
        this._ratingStatistics = new RatingStatisticsModel(ratingStatistics);
    }

    /**
     * @method
     * @description Returns the product
     * @returns {{productId: number; primaryId?: number; title: string; path: string; customizeIt: boolean; dynamicDimensions: boolean}}
     * @example
     * {
     *  productId: 123,
     *  primaryId: 456,
     *  title: "Product Title",
     *  path: "/product/path",
     *  customizeIt: true,
     *  dynamicDimensions: true
     * }
     */
    public get product(): {
        collectionName?: string;
        customizeIt: boolean;
        designerId?: number;
        dynamicDimensions: boolean;
        href: string;
        minQuantity: number;
        path: string;
        primaryId?: number;
        productId: number;
        sku: string,
        title: string
    } {
        return {
            ...this._product,
            customizeIt: this._product.customizeIt,
            designerId: this._product.designerId,
            dynamicDimensions: this._product.isDynamicDimension ?? false,
            href: this._product.customizeIt ? this._product.variantPath : `/shoppingcart/add?productid=${this._product.id}`,
            minQuantity: this._product.minQuantity,
            path: this._product.variantPath,
            productId: this._product.id,
            title: this._product.title ?? this._product.sku
        }
    }

    /**
     * @method
     * @description Returns the image
     * @returns {{src: string; alt: string; title?: string}}
     * @example
     * {
     *   src: "https://www.example.com/image.jpg",
     *   alt: "Image Alt Text",
     *   title: "Image Title"
     * }
     */
    public get image(): { src: string; alt: string; title?: string; } {
        return {
            ...this._image,
            src: this._image.url,
            alt: this._image.alt ?? this._product.sku
        }
    }

    /**
     * @method
     * @description Returns the price
     * @returns {{unitPrice: number; price: number; onSale: boolean; saleAmount?: number; salePercentage?: number; formatted: {unitPrice: string; price: string; saleAmount?: string; salePercentage?: string}}}
     * @example
     * {
     *  unitPrice: 9.99,
     *  price: 4.99,
     *  onSale: true,
     *  formatted: {
     *    unitPrice: "$9.99",
     *    price: "$4.99",
     *    ... (saleAmount and salePercentage if applicable)
     *  }
     * }
     */
    public get price(): {
        unitPrice: number;
        price: number;
        onSale: boolean;
        saleAmount?: number;
        salePercentage?: number;
        formatted: {
            unitPrice: string;
            price: string;
            saleAmount?: string;
            salePercentage?: string;
        };
    } {
        return {
            ...this._price,
            formatted: {
                price: currencyFormatter.format(this._price.price),
                unitPrice: currencyFormatter.format(this._price.unitPrice),
                saleAmount: this._price.saleAmount ? currencyFormatter.format(this._price.saleAmount) : undefined,
                salePercentage: this._price.salePercentage ? `${this._price.salePercentage}%` : undefined
            }
        }
    }

    /**
     * @method ratingStatistics
     * @description Returns the rating statistics for the product.
     * @returns {RatingStatisticsModel}
     */
    public get ratingStatistics(): RatingStatisticsModel {
        return this._ratingStatistics;
    }
}